/**
 * gs1-digital-link.ts
 *
 * Official GS1 Digital Link URI Builder, Modulo-10 Check Digit Validator,
 * and Dual-Mode Product Traceability Link Generator (Sprint CMS-KIOSK-THEME-MEDIA-01).
 *
 * Invariants:
 * - GS1 Digital Link URIs place the primary identification key (AI 01 GTIN-14) and
 *   qualifiers (AI 22, AI 10, AI 21) in the path, while descriptive attributes (AI 17, AI 15)
 *   belong in query parameters.
 * - Strict ASCII digit validation; non-digit or corrupted GTIN inputs are rejected.
 * - Non-GS1 internal catalog products use distinct internal product information links (/p/<id>).
 */

export type ProductQrIdentityMode = 'gs1_digital_link' | 'internal_product_link';

export interface Gs1DigitalLinkInput {
  readonly resolverBaseUrl: string;
  readonly gtin: string;
  readonly consumerVariant?: string; // AI (22)
  readonly lot?: string;             // AI (10)
  readonly serial?: string;          // AI (21)
  readonly expiryDate?: string;      // ISO YYYY-MM-DD -> converted to AI (17) YYMMDD
  readonly bestBeforeDate?: string;  // ISO YYYY-MM-DD -> converted to AI (15) YYMMDD
}

export interface ProductTraceabilityIdentity {
  readonly mode: ProductQrIdentityMode;
  readonly verifiedGtin?: string;
  readonly publicProductId: string;
  readonly gtinVerificationStatus: 'verified' | 'invalid' | 'not_provided' | 'not_applicable';
}

export function encodeGs1PathValue(value: string): string {
  if (!value || typeof value !== 'string') {
    throw new Error('GS1 qualifier value cannot be empty');
  }
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 20) {
    throw new Error('GS1 qualifier value must be between 1 and 20 characters');
  }
  // Reject control characters, newlines, and unprintable characters
  if (/[\x00-\x1F\x7F]/.test(trimmed)) {
    throw new Error('GS1 qualifier value contains prohibited control characters');
  }
  return encodeURIComponent(trimmed);
}

export class Gs1DigitalLinkBuilder {
  /**
   * Validates standard GS1 GTIN-8, GTIN-12, GTIN-13, or GTIN-14 check digit (Modulo 10).
   * Strict validation: rejects non-digit characters, whitespace, or invalid lengths.
   */
  public static validateGtinCheckDigit(gtin: string): boolean {
    if (!/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(gtin)) {
      return false;
    }

    const padded = gtin.padStart(14, '0');
    let sum = 0;

    for (let i = 0; i < 13; i += 1) {
      const digit = Number(padded[i]);
      sum += i % 2 === 0 ? digit * 3 : digit;
    }

    const expectedCheckDigit = (10 - (sum % 10)) % 10;
    return expectedCheckDigit === Number(padded[13]);
  }

  /**
   * Normalizes GTIN to a strict 14-digit numeric string with leading zeros.
   * Throws an error if input contains non-digits, has invalid length, or fails Modulo-10.
   */
  public static normalizeGtin14(gtin: string): string {
    const clean = gtin.trim();

    if (!/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(clean)) {
      throw new Error('GTIN must contain exactly 8, 12, 13, or 14 ASCII digits');
    }

    if (!this.validateGtinCheckDigit(clean)) {
      throw new Error('GTIN check digit is invalid');
    }

    return clean.padStart(14, '0');
  }

  /**
   * Strict ISO YYYY-MM-DD date parsing and conversion to GS1 YYMMDD format.
   * Rejects invalid calendar dates (e.g. 2026-02-31, 2026-19-99) and handles leap years.
   */
  public static toGs1YyMmDd(isoDate?: string): string | undefined {
    if (!isoDate) return undefined;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    if (!match) {
      throw new Error('Expected ISO date in YYYY-MM-DD form');
    }

    const [, year, month, day] = match;
    const parsed = new Date(`${isoDate}T00:00:00.000Z`);

    if (
      Number.isNaN(parsed.getTime()) ||
      parsed.getUTCFullYear() !== Number(year) ||
      parsed.getUTCMonth() + 1 !== Number(month) ||
      parsed.getUTCDate() !== Number(day)
    ) {
      throw new Error('Invalid calendar date');
    }

    return `${year.slice(-2)}${month}${day}`;
  }

  /**
   * Canonical GS1 Digital Link URI Builder:
   * Path: /01/<GTIN-14>[/22/<variant>][/10/<lot>][/21/<serial>]
   * Query: ?17=<YYMMDD>&15=<YYMMDD>
   */
  public static buildGs1DigitalLink(input: Gs1DigitalLinkInput): string {
    const gtin14 = this.normalizeGtin14(input.gtin);
    const pathParts = ['01', gtin14];

    if (input.consumerVariant) {
      pathParts.push('22', encodeGs1PathValue(input.consumerVariant));
    }
    if (input.lot) {
      pathParts.push('10', encodeGs1PathValue(input.lot));
    }
    if (input.serial) {
      pathParts.push('21', encodeGs1PathValue(input.serial));
    }

    const base = input.resolverBaseUrl.replace(/\/+$/, '');
    const url = new URL(pathParts.join('/'), `${base}/`);

    if (input.expiryDate) {
      const exp = this.toGs1YyMmDd(input.expiryDate);
      if (exp) url.searchParams.set('17', exp);
    }
    if (input.bestBeforeDate) {
      const bb = this.toGs1YyMmDd(input.bestBeforeDate);
      if (bb) url.searchParams.set('15', bb);
    }

    return url.toString();
  }

  /**
   * Internal Product Link URI (for catalog items without a verified GTIN).
   */
  public static buildInternalProductLink(resolverBaseUrl: string, publicProductId: string): string {
    const base = resolverBaseUrl.replace(/\/+$/, '');
    return `${base}/p/${encodeURIComponent(publicProductId.trim())}`;
  }
}
